import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import EntityToggle from './EntityToggle';
import {
  Home,
  Building2,
  Calendar,
  FileText,
  Wallet,
  Users,
  Settings,
  LogOut,
  BarChart3,
  ClipboardList,
  Award,
  UserCheck,
  DollarSign,
  Banknote,
  Shield,
  FileStack,
  UsersRound,
  FileEdit,
  Mail,
  Bell,
  FileCheck,
  Target,
  TrendingUp,
} from 'lucide-react';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const customerLinks = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/properties', label: 'Properties', icon: Building2 },
    { path: '/my-appointments', label: 'My Appointments', icon: Calendar },
    { path: '/my-bookings', label: 'My Bookings', icon: FileText },
    { path: '/wallet', label: 'Wallet', icon: Wallet },
    { path: '/referrals', label: 'Referrals', icon: Users },
    { path: '/profile', label: 'Profile', icon: Settings },
  ];

  const agentLinks = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/agent-properties', label: 'Properties', icon: Building2 },
    { path: '/appointments', label: 'Appointments', icon: Calendar },
    { path: '/bookings', label: 'Bookings', icon: FileText },
    { path: '/commission-claims', label: 'Commission Claims', icon: DollarSign },
    { path: '/payment-slips', label: 'Payment Slips', icon: FileCheck },
    { path: '/wallet', label: 'Wallet', icon: Wallet },
    { path: '/my-team', label: 'My Team', icon: Users },
    { path: '/performance', label: 'Performance', icon: BarChart3 },
    { path: '/profile', label: 'Profile', icon: Settings },
  ];

  const adminLinks = [
    { path: '/admin', label: 'Dashboard', icon: Home },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/commission-analytics', label: 'Commission Analytics', icon: DollarSign },
    { path: '/admin/properties', label: 'Properties', icon: Building2 },
    { path: '/admin/units', label: 'Unit Availability', icon: ClipboardList },
    { path: '/admin/booking-templates', label: 'Booking Forms', icon: FileEdit },
    { path: '/admin/developer-templates', label: 'Developer PDFs', icon: FileCheck },
    { path: '/admin/pdf-templates', label: 'PDF Templates', icon: FileStack },
    { path: '/admin/email-templates', label: 'Email Templates', icon: Mail },
    { path: '/admin/push-notifications', label: 'Push Notifications', icon: Bell },
    { path: '/admin/sam-rumah-group', label: 'SAM Rumah Group', icon: UsersRound },
    { path: '/admin/agents', label: 'Agent Management', icon: Award },
    { path: '/admin/customers', label: 'Customer Management', icon: UserCheck },
    { path: '/admin/promotions', label: 'Auto-Promote', icon: TrendingUp },
    { path: '/admin/rank-config', label: 'Rank Config', icon: Target },
    { path: '/admin/kyc', label: 'KYC Approvals', icon: UserCheck },
    { path: '/admin/appointments', label: 'Appointments', icon: Calendar },
    { path: '/admin/bookings', label: 'Bookings', icon: FileText },
    { path: '/admin/payment-verification', label: 'Payment Verification', icon: FileCheck },
    { path: '/admin/claims', label: 'Claims', icon: DollarSign },
    { path: '/admin/commission-claims', label: 'Commission Claims', icon: ClipboardList },
    { path: '/admin/withdrawals', label: 'Withdrawals', icon: Banknote },
    { path: '/admin/config', label: 'Configuration', icon: Shield },
  ];

  // PIC role - focused on claim review and approval
  const picLinks = [
    { path: '/admin', label: 'Dashboard', icon: Home },
    { path: '/admin/commission-claims', label: 'Commission Claims', icon: ClipboardList },
    { path: '/admin/bookings', label: 'Bookings', icon: FileText },
    { path: '/admin/properties', label: 'Properties', icon: Building2 },
    { path: '/profile', label: 'Profile', icon: Settings },
  ];

  // Account role - focused on payment verification, claim review and approval
  const accountLinks = [
    { path: '/admin', label: 'Dashboard', icon: Home },
    { path: '/admin/payment-verification', label: 'Payment Verification', icon: FileCheck },
    { path: '/admin/commission-claims', label: 'Commission Claims', icon: ClipboardList },
    { path: '/admin/bookings', label: 'Bookings', icon: FileText },
    { path: '/admin/claims', label: 'Claims', icon: DollarSign },
    { path: '/admin/withdrawals', label: 'Withdrawals', icon: Banknote },
    { path: '/profile', label: 'Profile', icon: Settings },
  ];

  // Determine which links to show based on role
  const getLinksForRole = (role) => {
    switch (role) {
      case 'admin':
        return adminLinks;
      case 'pic':
        return picLinks;
      case 'account':
        return accountLinks;
      case 'agent':
        return agentLinks;
      default:
        return customerLinks;
    }
  };

  const links = getLinksForRole(user?.role);

  return (
    <aside className="fixed left-0 top-0 h-screen w-56 bg-white border-r border-gray-200 flex flex-col z-40 shadow-sm" data-testid="sidebar">
      {/* Logo */}
      <div className="p-5 border-b border-gray-100">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 bg-gradient-primary rounded-xl flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-gradient-brand">Sam Rumah</span>
        </Link>
      </div>

      {/* User Info */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#4E50FF]/10 flex items-center justify-center">
            <span className="text-[#4E50FF] font-semibold text-sm">
              {user?.full_name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.full_name}</p>
            <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            {user?.agent_code && (
              <p className="text-xs text-[#4E50FF] font-mono font-medium">{user.agent_code}</p>
            )}
          </div>
        </div>
      </div>

      {/* Entity Toggle - Admin/PIC/Account only */}
      {['admin', 'pic', 'account'].includes(user?.role) && (
        <div className="px-3 py-2 border-b border-gray-100">
          <EntityToggle />
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {links.map((link) => {
          const Icon = link.icon;
          const active = isActive(link.path);
          return (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.path.replace(/\//g, '-')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                active 
                  ? 'bg-[#4E50FF] text-white shadow-md shadow-[#4E50FF]/25' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{link.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-gray-100">
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium w-full text-red-600 hover:bg-red-50 transition-all duration-200"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
